import React, { useEffect, useState } from "react"
import { Occasion, Filters } from "./App"
import CheckboxLocation from "./CheckboxLocation"
import { formatPrice } from "./util"

interface FilterProps {
    occasions: Occasion[]
    onReset: () => void
    filters: Filters
    onChange: (
        type: "checkbox" | "select" | "checkbox-location-based",
        key: string,
        termId: number
    ) => void
}

export interface Taxonomy {
    label: string
    type: "checkbox" | "select" | "checkbox-location-based"
    items:
        | {
              term_id: number
              name: string
              hq?: {
                  latitude: string
                  longitude: string
              }
              km?: string
          }[]
        | number[]
}

interface Taxonomies {
    category: Taxonomy
    year: Taxonomy
    price: Taxonomy
    kilometers: Taxonomy
    color: Taxonomy
    fuel: Taxonomy
    milieulabel: Taxonomy
    transmission: Taxonomy
    type: Taxonomy
    supplier?: Taxonomy
}

export default function Filter({
    occasions,
    onReset,
    filters,
    onChange,
}: FilterProps) {
    const isDesktop = window.innerWidth > 998
    const [isOpen, setIsOpen] = useState(isDesktop)
    const [locationBased, setLocationBased] = useState<Taxonomy["items"]>(
        occasions
            .map((it) => it.supplier)
            .filter(isUniqueTaxonomyInArray)
            .sort(sortNameDesc)
    )

    const taxonomies = React.useMemo(() => {
        let base: Taxonomies = {
            category: {
                label: "Merk",
                type: "checkbox",
                items: occasions
                    .map((it) => it.category)
                    .filter(isUniqueTaxonomyInArray)
                    .sort(sortNameDesc),
            },

            year: {
                label: "Bouwjaar",
                type: "select",
                items: occasions
                    .map((it) => parseInt(it.year))
                    .filter(isUniqueInArray)
                    .sort(sortDesc),
            },
            price: {
                label: "Prijs",
                type: "select",
                items: [
                    500, 1000, 1500, 2000, 2500, 3000, 4000, 5000, 6000, 7000,
                    8000, 9000, 10000, 12500, 15000, 17500, 20000, 25000, 30000,
                    40000, 50000, 75000, 100000,
                ],
            },
            kilometers: {
                label: "Kilometerstand",
                type: "select",
                items: [
                    2500, 5000, 10000, 20000, 30000, 40000, 50000, 60000, 70000,
                    80000, 90000, 100000, 125000, 150000, 175000, 200000,
                ],
            },

            color: {
                label: "Kleur",
                type: "checkbox",
                items: occasions
                    .map((it) => it.color)
                    .filter(isUniqueTaxonomyInArray)
                    .sort(sortNameDesc),
            },
            fuel: {
                label: "Brandstof",
                type: "checkbox",
                items: occasions
                    .map((it) => it.fuel)
                    .filter(isUniqueTaxonomyInArray)
                    .sort(sortNameDesc),
            },
            milieulabel: {
                label: "Milieulabel",
                type: "checkbox",
                items: occasions
                    .map((it) => it.milieulabel)
                    .filter(isUniqueTaxonomyInArray)
                    .sort(sortNameDesc),
            },
            transmission: {
                label: "Transmissie",
                type: "checkbox",
                items: occasions
                    .map((it) => it.transmission)
                    .filter(isUniqueTaxonomyInArray)
                    .sort(sortNameDesc),
            },
            type: {
                label: "Carrosserie",
                type: "checkbox",
                items: occasions
                    .map((it) => it.type)
                    .filter(isUniqueTaxonomyInArray)
                    .sort(sortNameDesc),
            },
        }

        if (window.local_occasions_object.is_corporate) {
            base = {
                supplier: {
                    label: "Leverancier",
                    type: "checkbox-location-based",
                    items: locationBased,
                },
                ...base,
            }
        }

        return base
    }, [locationBased])

    useEffect(() => {
        if (!isDesktop) {
            if (isOpen) {
                document.body.classList.add("overflow-hidden")
            } else {
                document.body.classList.remove("overflow-hidden")
            }
        }
    }, [isOpen])

    useEffect(() => {
        function handleResize() {
            if (window.innerWidth > 998) {
                setIsOpen(true)
            } else {
                setIsOpen(false)
            }
        }
        window.addEventListener("resize", handleResize)

        return () => window.removeEventListener("resize", handleResize)
    }, [])

    if (!isOpen) {
        return (
            <div className="fixed bottom-0 left-0 right-0 m-auto z-10 flex">
                <button
                    className="button mb-4 mx-auto"
                    onClick={() => setIsOpen(true)}
                    type="button"
                >
                    Filter occasions
                </button>
            </div>
        )
    }

    return (
        <div className="bg-white shadow-no-offset py-5 px-5 lg:relative fixed left-0 top-0 w-full bg-light z-50 lg:h-auto h-full overflow-scroll">
            <h4 className="mb-1">Filteren</h4>
            <button className="underline mb-5" onClick={onReset}>
                Alle filters wissen
            </button>

            <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="lg:hidden flex fixed z-20 right-5 top-5 px-3 py-1 bg-dark text-light rounded-full items-center justify-center"
            >
                <span className="text-xs mr-1">
                    Bekijk de {occasions.length} resultaten
                </span>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    className="feather feather-chevron-right -mr-1"
                    viewBox="0 0 24 24"
                >
                    <path d="M9 18L15 12 9 6"></path>
                </svg>
            </button>

            {Object.entries(taxonomies).map(([key, taxonomy]) => {
                if (taxonomy.type === "select") {
                    const startKey = `${key}_start`
                    const endKey = `${key}_end`

                    return (
                        <div
                            key={key}
                            className="taxonomy select text-[#8C8C8C] mb-5"
                        >
                            <strong className="taxonomy-label block mb-2">
                                {taxonomy.label}
                            </strong>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="select-wrapper with-chevron">
                                    <select
                                        className="py-2"
                                        name={startKey}
                                        value={
                                            filters[startKey]
                                                ? filters[startKey]
                                                : ""
                                        }
                                        onChange={(e) => {
                                            onChange(
                                                "select",
                                                startKey,
                                                e.target.value === ""
                                                    ? undefined
                                                    : parseInt(e.target.value)
                                            )
                                        }}
                                    >
                                        <option value="">van</option>
                                        {taxonomy.items.map((item) => (
                                            <option key={item} value={item}>
                                                {key === "year"
                                                    ? item
                                                    : formatPrice(item)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="select-wrapper with-chevron">
                                    <select
                                        className="py-2"
                                        name={endKey}
                                        value={
                                            filters[endKey]
                                                ? filters[endKey]
                                                : ""
                                        }
                                        onChange={(e) => {
                                            onChange(
                                                "select",
                                                endKey,
                                                e.target.value === ""
                                                    ? undefined
                                                    : parseInt(e.target.value)
                                            )
                                        }}
                                    >
                                        <option value="">tot</option>
                                        {taxonomy.items.map((item, _key) => {
                                            if (filters[startKey]) {
                                                const removeUntil =
                                                    taxonomy.items.findIndex(
                                                        (_it) =>
                                                            _it ===
                                                            filters[startKey]
                                                    )

                                                console.log(
                                                    filters[startKey],
                                                    taxonomy.items
                                                )
                                                if (_key < removeUntil)
                                                    return null
                                            }

                                            return (
                                                <option key={item} value={item}>
                                                    {key === "year"
                                                        ? item
                                                        : formatPrice(item)}
                                                </option>
                                            )
                                        })}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )
                }

                if (taxonomy.type === "checkbox-location-based") {
                    return (
                        <CheckboxLocation
                            key={key}
                            filters={filters}
                            onChange={onChange}
                            filtersKey={key}
                            taxonomy={taxonomy}
                            onTaxonomyKmUpdate={(taxonomy) => {
                                // @ts-ignore
                                setLocationBased([...taxonomy.items])
                            }}
                        />
                    )
                }

                return (
                    <div
                        className="taxonomy mb-5 last:mb-0 text-[#8C8C8C]"
                        key={key}
                    >
                        <strong className="taxonomy-label block mb-2">
                            {taxonomy.label}
                        </strong>
                        <div className="taxonomy-list flex flex-col gap-y-1 max-h-[155px] overflow-scroll">
                            {taxonomy.items.map((item) => (
                                <label
                                    className="flex items-center select-none cursor-pointer mb-1"
                                    key={item.term_id}
                                    onClick={() =>
                                        onChange("checkbox", key, item.term_id)
                                    }
                                >
                                    {filters[key].includes(item.term_id) ? (
                                        <div className="w-[18px] h-[18px] border border-primary bg-primary rounded mr-3" />
                                    ) : (
                                        <div className="w-[18px] h-[18px] border border-[#b6b6b6] rounded mr-3" />
                                    )}
                                    <span className="block leading-none mt-[1px]">
                                        {item.name}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

function isUniqueTaxonomyInArray(taxonomy, index, self) {
    if (!taxonomy) return false
    // Suppliers have no slug, so fall back to term_id
    const key = (t) => t?.slug ?? t?.term_id
    return index === self.findIndex((t) => key(t) === key(taxonomy))
}

function isUniqueInArray(value, index, self) {
    if (!value) return false
    return index === self.findIndex((t) => t === value)
}

function sortDesc(a, b) {
    if (a < b) return -1
    if (a > b) return 1
    return 0
}

function sortNameDesc(a, b) {
    if (a.name < b.name) return -1
    if (a.name > b.name) return 1
    return 0
}
